import { useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Plus, Pencil, Trash2, Upload } from 'lucide-react'
import { storyCardsApi } from '../../../services/api'
import { ADMIN_STORY_CARD_MODULES } from '../../../constants/adminModules'
import Button from '../../../components/ui/Button'
import LoadingSpinner from '../../../components/ui/LoadingSpinner'
import Card from '../../../components/ui/Card'
import AdminFormField, { adminInputClass } from '../../../components/admin/AdminFormField'
import MediaImage from '../../../components/ui/MediaImage'
import { previewImageSrc, stripStoragePrefix } from '../../../utils/media'

const TYPE_BY_ROUTE = Object.fromEntries(
  ADMIN_STORY_CARD_MODULES.map(({ route, type }) => [route, type]),
)

const PROGRAMS = ['british', 'american', 'national']

function buildStoryCardFormData(form, imageFile) {
  const fd = new FormData()
  ;[
    'type',
    'name_en',
    'name_ar',
    'meta_en',
    'meta_ar',
    'quote_en',
    'quote_ar',
    'program',
  ].forEach((field) => {
    const value = form[field]
    if (value != null && value !== '') fd.append(field, value)
  })
  fd.append('sort_order', String(form.sort_order ?? 0))
  fd.append('is_active', form.is_active ? '1' : '0')
  if (imageFile) fd.append('image', imageFile)
  else if (form.image) fd.append('image', stripStoragePrefix(form.image))
  return fd
}

function emptyForm(type) {
  return {
    type,
    name_en: '',
    name_ar: '',
    meta_en: '',
    meta_ar: '',
    quote_en: '',
    quote_ar: '',
    image: '',
    program: 'british',
    sort_order: 0,
    is_active: true,
  }
}

export default function AdminStoryCards() {
  const { cardType } = useParams()
  const type = TYPE_BY_ROUTE[cardType] || 'alumni'
  const { t } = useTranslation()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [form, setForm] = useState(null)
  const [imageFile, setImageFile] = useState(null)
  const [imagePreview, setImagePreview] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const fileRef = useRef(null)

  const load = () => {
    setLoading(true)
    storyCardsApi
      .getAllAdmin({ type })
      .then(({ data }) => setItems(data.data || data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(load, [type])

  useEffect(() => {
    return () => {
      if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    }
  }, [imagePreview])

  const openForm = (item = null) => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(null)
    setImagePreview(null)
    setError('')
    setForm(item ? { ...emptyForm(type), ...item, type } : emptyForm(type))
  }

  const closeForm = () => {
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setForm(null)
    setImageFile(null)
    setImagePreview(null)
    setError('')
  }

  const handleImageSelect = (event) => {
    const file = event.target.files?.[0]
    if (!file) return
    if (imagePreview?.startsWith('blob:')) URL.revokeObjectURL(imagePreview)
    setImageFile(file)
    setImagePreview(URL.createObjectURL(file))
    event.target.value = ''
  }

  const handleSave = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      const payload = buildStoryCardFormData({ ...form, type }, imageFile)
      if (form.id) await storyCardsApi.update(form.id, payload)
      else await storyCardsApi.create(payload)
      closeForm()
      load()
    } catch (err) {
      setError(
        err.response?.data?.message ||
          Object.values(err.response?.data?.errors || {})?.flat()?.[0] ||
          'Failed to save card.',
      )
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm(t('admin.storyCards.confirmDelete'))) return
    await storyCardsApi.delete(id)
    load()
  }

  const currentPreview = previewImageSrc(form?.image, imagePreview)

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-lg font-semibold text-primary">{t(`admin.storyCards.${type}Title`)}</h2>
        <Button onClick={() => openForm()} variant="accent" size="sm">
          <Plus className="h-4 w-4" />
          {t('admin.storyCards.add')}
        </Button>
      </div>

      {form && (
        <Card className="mb-8">
          <form onSubmit={handleSave} className="grid gap-4 sm:grid-cols-2">
            <AdminFormField label={t('admin.pages.titleEn')} htmlFor="card-name-en">
              <input id="card-name-en" value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} className={adminInputClass} required />
            </AdminFormField>
            <AdminFormField label={t('admin.pages.titleAr')} htmlFor="card-name-ar">
              <input id="card-name-ar" value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} required />
            </AdminFormField>
            <AdminFormField label={t('admin.storyCards.metaEn')} htmlFor="card-meta-en">
              <input id="card-meta-en" value={form.meta_en || ''} onChange={(e) => setForm({ ...form, meta_en: e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.storyCards.metaAr')} htmlFor="card-meta-ar">
              <input id="card-meta-ar" value={form.meta_ar || ''} onChange={(e) => setForm({ ...form, meta_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} />
            </AdminFormField>
            <AdminFormField label={t('admin.storyCards.quoteEn')} htmlFor="card-quote-en" className="sm:col-span-2">
              <textarea id="card-quote-en" rows={4} value={form.quote_en} onChange={(e) => setForm({ ...form, quote_en: e.target.value })} className={adminInputClass} required />
            </AdminFormField>
            <AdminFormField label={t('admin.storyCards.quoteAr')} htmlFor="card-quote-ar" className="sm:col-span-2">
              <textarea id="card-quote-ar" rows={4} value={form.quote_ar} onChange={(e) => setForm({ ...form, quote_ar: e.target.value })} dir="rtl" lang="ar" className={`${adminInputClass} font-ar`} required />
            </AdminFormField>
            <AdminFormField label={t('admin.storyCards.program')} htmlFor="card-program">
              <select id="card-program" value={form.program || ''} onChange={(e) => setForm({ ...form, program: e.target.value })} className={adminInputClass}>
                {PROGRAMS.map((program) => (
                  <option key={program} value={program}>{t(`programs.${program}.title`)}</option>
                ))}
              </select>
            </AdminFormField>
            <AdminFormField label={t('admin.slidersAdmin.order')} htmlFor="card-order">
              <input id="card-order" type="number" value={form.sort_order ?? 0} onChange={(e) => setForm({ ...form, sort_order: +e.target.value })} className={adminInputClass} />
            </AdminFormField>
            <AdminFormField label={t('admin.slidersAdmin.image')} htmlFor="card-image" className="sm:col-span-2">
              <div className="space-y-3">
                {currentPreview ? (
                  <MediaImage src={currentPreview} alt="" className="h-40 w-full max-w-xs rounded-xl border border-border object-cover" />
                ) : null}
                <input id="card-image" value={form.image || ''} onChange={(e) => setForm({ ...form, image: e.target.value })} placeholder={t('admin.slidersAdmin.imageUrl')} className={adminInputClass} />
                <div className="flex flex-wrap items-center gap-3">
                  <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleImageSelect} />
                  <Button type="button" variant="outlineDark" size="sm" onClick={() => fileRef.current?.click()} disabled={saving}>
                    <Upload className="h-4 w-4" />
                    {t('admin.slidersAdmin.uploadImage')}
                  </Button>
                  {imageFile ? <span className="text-xs text-text/60">{imageFile.name}</span> : null}
                </div>
              </div>
            </AdminFormField>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <input type="checkbox" checked={Boolean(form.is_active)} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              {t('admin.menus.active')}
            </label>
            {error ? <p className="text-sm text-red-600 sm:col-span-2">{error}</p> : null}
            <div className="flex gap-2 sm:col-span-2">
              <Button type="submit" variant="primary" disabled={saving}>{t('buttons.save')}</Button>
              <Button type="button" variant="ghost" onClick={closeForm}>{t('buttons.cancel')}</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <LoadingSpinner className="py-20" />
      ) : items.length === 0 ? (
        <Card className="py-12 text-center text-text/60">{t('admin.table.noData')}</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((item) => (
            <Card key={item.id} padding={false} className="overflow-hidden">
              <MediaImage
                src={item.image}
                alt={item.name_en}
                className="h-40 w-full object-cover"
                fallbackIndex={item.sort_order ?? 0}
              />
              <div className="flex items-start justify-between gap-2 p-4">
                <div className="min-w-0">
                  <div className="truncate font-medium text-primary">{item.name_en}</div>
                  <div className="truncate text-xs text-text/50">{item.meta_en}</div>
                  {!item.is_active ? <div className="mt-1 text-xs text-red-500">{t('admin.homepage.inactive')}</div> : null}
                </div>
                <div className="flex shrink-0 gap-1">
                  <button type="button" onClick={() => openForm(item)} className="p-2 text-primary"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => handleDelete(item.id)} className="p-2 text-red-600"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
